import type { RouteLocationNormalized } from 'vue-router'
import type { RouteGuard, GuardResult } from './RouteGuard'
import { useAuthStore } from '@/stores/auth'
import { useLegalConsentStore } from '@/stores/legalConsent'
import { authReady } from '@/services/firebase/config'

/**
 * Rutas autenticadas que NO exigen el consentimiento: `/verify-email` es parte
 * del alta (el registro ya registró la aceptación con la casilla). Las públicas
 * (`meta.requiresAuth === false`: login, registro, recuperar contraseña) quedan
 * exentas por su propio meta.
 */
const CONSENT_EXEMPT_ROUTES = new Set(['verify-email'])

/**
 * Guard de consentimiento de los Términos y la Política de privacidad
 * (ADR-SEC-013 local, HU-AUTH-006, TRX-007).
 *
 * Al iniciar o restaurar sesión carga y ESPERA `users/{uid}` y
 * `legaldocs_current_version/current` (del servidor) y, si el usuario no ha
 * aceptado la versión vigente, retiene la navegación hasta que decida en el
 * modal: «Continuar» la deja seguir; «Salir» cierra la sesión y lleva a `/login`.
 * El resultado se recuerda por `uid` durante la sesión: no relee en cada
 * navegación. Fail-open si falta `current` (decisión del owner).
 */
export class RequiresLegalConsentGuard implements RouteGuard {
  name = 'requiresLegalConsent'

  async canActivate(to: RouteLocationNormalized): Promise<GuardResult> {
    if (to.meta.requiresAuth === false) return true
    if (typeof to.name === 'string' && CONSENT_EXEMPT_ROUTES.has(to.name)) return true

    await authReady

    const authStore = useAuthStore()
    const firebaseUser = authStore.user
    if (!firebaseUser) return true // RequiresAuthGuard ya decidió.

    const outcome = await useLegalConsentStore().ensureConsent(firebaseUser)
    if (outcome === 'exited') return '/login'

    // «Un último paso» puede haber aprovisionado `users/{uid}` recién ahora.
    if (!authStore.userDocument) await authStore.loadUserDocument(true)
    return true
  }
}
